import { Box, CircularProgress, makeStyles, Theme } from '@material-ui/core'
import axios, { CancelTokenSource } from 'axios'
import React, { Suspense, useContext, useEffect } from 'react'
import { fetchStatusIs } from '../api/types'
import AppContext from '../app/context'
import useFetchImage from '../hooks/useFetchImage'
import AppSpinner from './AppSpinner'

export interface ViewImageProps {
  imageId?: string | null
  imageName?: string | null
  defaultImage?: JSX.Element
  className?: string
  alt?: string
  shouldContain?: boolean
}

const stylesViewImage = makeStyles((theme: Theme) => ({
  image: (props: { shouldContain: boolean }) => {
    return {
      width: '100%',
      height: '100%',
      objectFit: props.shouldContain ? 'contain' : 'cover',
    }
  },
  default: {
    background: theme.palette.background.paper,
  },
}))

// eslint-disable-next-line react/display-name
const ViewImage = ({
  className,
  imageId,
  defaultImage,
  imageName,
  alt,
  shouldContain,
}: ViewImageProps) => {
  const { state } = useContext(AppContext)
  const styles = stylesViewImage({ shouldContain: shouldContain ?? false })
  const cancelToken: CancelTokenSource = axios.CancelToken.source()
  // const [image, setImage] = React.useState<string | ArrayBuffer | null>(null)
  const controller = new AbortController()
  const signal = controller.signal

  const { requestStatus, image } = useFetchImage(imageId, signal)
  const { fulfilled, pending, rejected } = fetchStatusIs(requestStatus)

  useEffect(() => {
    return () => {
      if (!controller.signal.aborted) controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      cancelToken.cancel('Api request is being canceled')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!state.locationDesignConfig) return null

  return (
    <>
      <Suspense fallback={<AppSpinner />}>
        {pending && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width={50}
            height={50}
            borderRadius={50}
            className={styles.default}
          >
            <CircularProgress
              style={{ color: state.locationDesignConfig.baseColors.primary }}
            />
          </Box>
        )}

        {fulfilled && !!imageId && !!image && (
          <img
            alt={imageName ?? alt ?? 'preview'}
            className={`${styles.image} ${className}`}
            src={`${image}`}
          />
        )}

        {(!imageId || rejected) && <>{defaultImage}</>}
      </Suspense>
    </>
  )
}

export default ViewImage
